import React, {useEffect, useState} from 'react';
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import {Card} from "../../../../../../components/card/card.jsx";
import Button from "../../../../../../components/button/button.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import InputTextarea from "../../../../../../components/input/input-textarea.jsx";
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import {MaskDefault} from "../../../../../../utils/mask.js";
import optionsUtils from "../../../../../../utils/options-utils.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import {getCities, getNeighborhoods, getStreets} from "../../../../../../utils/location-utils.js";
import ModalReverseGeocoding from "../../../../../../components/modal/modal-reverse-geocoding.jsx";
import citizenScheduledVisitService from "../../../../../../services/web/citizen-scheduled-visit-service.js";
import userService from "../../../../../../services/web/user-service.js";
import locationService from "../../../../../../services/web/location-service.js";
import {isRetroactive, SCHEDULE_TYPE_OPTIONS, translateScheduledVisitError} from "../../utils/schedule-utils.js";
import {format, startOfToday} from "date-fns";
import Alert from "../../../../../../components/alert/alert.jsx";
import Swal from "sweetalert2";
import {FaExternalLinkAlt} from "react-icons/fa";
import useHasPermission from "../../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../../utils/enums/permission-enum.js";

const formatPrograms = (programs) => {
    if (Array.isArray(programs)) return programs.map(p => p?.label ?? p).join(", ");
    return programs ?? "";
};

const emptyForm = {
    citizenId: null,
    type: "",
    date: "",
    startTime: "",
    endTime: "",
    addressType: "",
    address: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    zipCode: "",
    latitude: "",
    longitude: "",
};

const emptyExecution = {executionUserId: "", executionDt: "", executionDescription: ""};

const CitizenScheduledVisitEditModal = ({id, open, setOpen, onChanged, onSaved, openDispatch}) => {
    const hasPermission = useHasPermission();
    const queryClient = useQueryClient();

    const [form, setForm] = useState(emptyForm);
    const [isSaving, setIsSaving] = useState(false);

    const [mapOpen, setMapOpen] = useState(false);

    const [executionOpen, setExecutionOpen] = useState(false);
    const [execution, setExecution] = useState(emptyExecution);

    const {data: detail, isFetching: isLoadingDetail} = useQuery({
        queryFn: () => citizenScheduledVisitService.get(id),
        queryKey: ["citizen-scheduled-visit-detail", id],
        enabled: !!id && open,
    });

    const {data: locationTypes} = useQuery({
        queryFn: () => locationService.getLocationType().then(data => data.map(({id, text}) => ({value: id, label: text}))),
        queryKey: ["location-types"],
        enabled: open,
    });

    const {data: agents} = useQuery({
        queryFn: () => userService.optionsActiveUserName(),
        queryKey: ["active-users-options"],
        enabled: executionOpen,
    });

    useEffect(() => {
        setForm(emptyForm);
    }, [id, open]);

    useEffect(() => {
        if (!detail || !open) return;

        const start = detail.scheduleWindowStartDt ? new Date(detail.scheduleWindowStartDt) : null;
        const end = detail.scheduleWindowEndDt ? new Date(detail.scheduleWindowEndDt) : null;

        setForm({
            citizenId: detail.citizenId,
            type: detail.type ?? "",
            date: start ? format(start, "dd/MM/yyyy") : "",
            startTime: start ? format(start, "HH:mm") : "",
            endTime: end ? format(end, "HH:mm") : "",
            addressType: detail.addressType ?? "",
            address: detail.address ?? "",
            number: detail.number ?? "",
            complement: detail.complement ?? "",
            neighborhood: detail.neighborhood ?? "",
            city: detail.city ?? "",
            zipCode: detail.zipCode ?? "",
            latitude: detail.latitude ?? "",
            longitude: detail.longitude ?? "",
        });
    }, [detail, open]);

    const handleInput = ({target: {id: field, value}}) => setForm(prev => ({...prev, [field]: value}));

    const handleField = (field, value) => setForm(prev => ({...prev, [field]: value ?? ""}));

    const updateLatLng = (val) => {
        const city = (val.city && val.district) ? `${val.city} - ${val.district}` : null;

        setForm(prev => ({
            ...prev,
            latitude: val.latitude,
            longitude: val.longitude,
            ...(val.changeAddress && {
                city,
                address: val.street,
                number: val.number,
                neighborhood: val.neighborhood,
            })
        }));
    };

    const finish = (message, closeEditWindow = false) => {
        projectUtils.success(false, message);
        queryClient.invalidateQueries({queryKey: ["citizen-scheduled-visit-detail", id]});
        onChanged?.();
        if (closeEditWindow && onSaved?.()) return;
        setOpen(false);
    };

    const handleError = (error, fallback) => {
        console.error(error);
        const message = error.response?.status !== 500
            ? translateScheduledVisitError(error.response?.data?.message, fallback)
            : fallback;
        projectUtils.swalErrorMessage('', message, 'Ok');
    };

    const save = () => {
        if (!form.type) {
            projectUtils.swalErrorMessage('', "Selecione o tipo de agendamento.", "Ok");
            return;
        }

        if (!form.date || !form.startTime || !form.endTime) {
            projectUtils.swalErrorMessage('', "Informe a data e o intervalo de horário.", "Ok");
            return;
        }

        if (form.startTime >= form.endTime) {
            projectUtils.swalErrorMessage('', "O horário final deve ser maior que o inicial.", "Ok");
            return;
        }

        if (isRetroactive(form)) {
            projectUtils.swalErrorMessage('', "O Data/hora do agendamento deve ser maior que o atual.", "Ok");
            return;
        }

        const {date, startTime, endTime, ...rest} = form;
        const payload = {
            ...rest,
            scheduleWindowStartDt: `${date} ${startTime}`,
            scheduleWindowEndDt: `${date} ${endTime}`,
        };

        setIsSaving(true);
        citizenScheduledVisitService.edit(id, projectUtils.replaceEmptyStringsWithNull(payload))
            .then(() => finish("Salvo com sucesso", true))
            .catch((error) => handleError(error, "Não foi possível salvar o agendamento."))
            .finally(() => setIsSaving(false));
    };

    const remove = () => {
        Swal.fire({
            title: "Atenção",
            text: "Deseja cancelar este agendamento?",
            icon: "warning",
            input: "textarea",
            inputLabel: "Justificativa para o cancelamento:",
            confirmButtonText: "Continuar",
            confirmButtonColor: '#063A6C',
            cancelButtonText: "Cancelar",
            showCancelButton: true,
            reverseButtons: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            preConfirm(inputValue) {
                if (!inputValue) {
                    Swal.showValidationMessage("Escreva uma justificativa!");
                }
            }
        }).then(({isConfirmed, value}) => {
            if (!isConfirmed) return;

            setIsSaving(true);
            citizenScheduledVisitService.cancel(id, value)
                .then(() => finish("Cancelado com sucesso"))
                .catch((error) => {
                    if (error?.response?.data?.code === 400) {
                        handleError(error, error.response.data.message);
                    } else {
                        handleError(error, "Não foi possível cancelar o agendamento.");
                    }
                })
                .finally(() => setIsSaving(false));
        });
    };

    const openExecution = () => {
        setExecution({...emptyExecution, executionDt: projectUtils.formatDate(new Date())});
        setExecutionOpen(true);
    };

    const confirmExecution = () => {
        if (!execution.executionUserId) {
            projectUtils.swalErrorMessage('', "O campo \"Realizado por\" é obrigatório.", "Ok");
            return;
        }

        if (!execution.executionDt) {
            projectUtils.swalErrorMessage('', "O campo \"D/H da execução\" é obrigatório.", "Ok");
            return;
        }

        setIsSaving(true);
        citizenScheduledVisitService.execute(id, execution)
            .then(() => {
                setExecutionOpen(false);
                finish("Finalizado com sucesso");
            })
            .catch((error)  => {
                if(error?.response?.data?.code === 400) {
                    handleError(error, error.response.data.message);
                } else {
                    handleError(error, "Não foi possível concluir o agendamento.");
                }
            })
            .finally(() => setIsSaving(false));
    };

    const disableButtons = isSaving || isLoadingDetail || detail?.inProgress || detail?.assigment;

    return (
        <>
            <Modal open={open} setOpen={setOpen} title={"Editar agendamento"} className={"w-5xl"} overflow={true}>
                <Modal.Body className={"space-y-4"}>
                    <Card>
                        <Card.Header>
                            <Card.Title>Protegido</Card.Title>
                        </Card.Header>
                        <Card.Body className={"grid grid-cols-12 gap-4"}>
                            <InputForm
                                classNameWrapper={"col-span-5"}
                                id={"citizenName"}
                                label={"Nome"}
                                value={detail?.citizenName ?? ""}
                                disabled
                            />

                            <InputForm
                                classNameWrapper={"col-span-4"}
                                id={"protectionPrograms"}
                                label={"Programa de proteção"}
                                value={formatPrograms(detail?.protectionPrograms)}
                                disabled
                            />

                            <InputForm
                                classNameWrapper={"col-span-3"}
                                id={"risk"}
                                label={"Grau de risco"}
                                value={detail?.risk}
                                disabled
                            />
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header>
                            <Card.Title>Definições</Card.Title>
                        </Card.Header>
                        <Card.Body className={"grid grid-cols-12 gap-4"}>
                            <SelectComponent
                                classNameWrapper={"col-span-3"}
                                value={form.type}
                                options={SCHEDULE_TYPE_OPTIONS}
                                label={"Tipo *"}
                                onChange={(value) => handleField("type", value?.value)}
                                isClearable={false}
                            />

                            <InputDatetime
                                classNameWrapper={"col-span-3"}
                                id={"date"}
                                label={"Data *"}
                                dateFormat={"L"}
                                returnDateObj={false}
                                restrictions={{before: startOfToday()}}
                                selected={form.date}
                                onChange={(value) => handleField("date", value)}
                            />

                            <InputDatetime
                                classNameWrapper={"col-span-3"}
                                id={"startTime"}
                                label={"Horário inicial *"}
                                dateFormat={"LT"}
                                returnDateObj={false}
                                selected={form.startTime}
                                onChange={(value) => handleField("startTime", value)}
                            />

                            <InputDatetime
                                classNameWrapper={"col-span-3"}
                                id={"endTime"}
                                label={"Horário final *"}
                                dateFormat={"LT"}
                                returnDateObj={false}
                                selected={form.endTime}
                                onChange={(value) => handleField("endTime", value)}
                            />
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header>
                            <Card.Title>Endereço</Card.Title>
                        </Card.Header>
                        <Card.Body className={"grid grid-cols-12 gap-4"}>
                            <SelectComponent
                                classNameWrapper={"col-span-4"}
                                value={form.addressType}
                                options={locationTypes ?? []}
                                label={"Tipo de local"}
                                onChange={(selected) => handleField("addressType", selected?.value)}
                            />

                            <SelectComponent
                                classNameWrapper={"col-span-5"}
                                loadOptions={getStreets}
                                value={optionsUtils.getValue(form.address)}
                                isAsync={true}
                                label={"Logradouro"}
                                onChange={(selected) => handleField("address", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />

                            <InputForm classNameWrapper={"col-span-3"} id={"number"} label={"Número"} value={form.number} onChange={handleInput}/>

                            <SelectComponent
                                classNameWrapper={"col-span-4"}
                                loadOptions={getNeighborhoods}
                                value={optionsUtils.getValue(form.neighborhood)}
                                isAsync={true}
                                label={"Bairro"}
                                onChange={(selected) => handleField("neighborhood", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />

                            <InputForm classNameWrapper={"col-span-3"} id={"complement"} label={"Complemento"} value={form.complement} onChange={handleInput}/>

                            <SelectComponent
                                classNameWrapper={"col-span-5"}
                                loadOptions={getCities}
                                value={optionsUtils.getValue(form.city)}
                                isAsync={true}
                                label={"Município"}
                                onChange={(selected) => handleField("city", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />

                            <InputForm classNameWrapper={"col-span-4"} id={"zipCode"} label={"CEP"} value={form.zipCode} onChange={handleInput} mask={MaskDefault.cep}/>
                            <InputForm classNameWrapper={"col-span-4"} id={"latitude"} label={"Latitude"} value={form.latitude} onChange={handleInput}/>
                            <InputForm classNameWrapper={"col-span-4"} id={"longitude"} label={"Longitude"} value={form.longitude} onChange={handleInput}/>

                            <Button target={"primary"} className={"col-span-4"} onClick={() => setMapOpen(true)}>
                                Mapa
                            </Button>
                        </Card.Body>
                    </Card>

                    {(detail?.dispatchId && hasPermission(PermissionEnum.DISPATCH_DISPATCH)) && (
                        <div className={"col-span-12 flex items-center gap-2"}>
                            <span className={"text-sm text-gray-500"}>Despacho vinculado:</span>
                            <button
                                type={"button"}
                                onClick={() => openDispatch(detail?.dispatchId)}
                                className={"inline-flex items-center gap-1 text-sm font-semibold text-[#063A6C] hover:underline cursor-pointer"}
                            >
                                <FaExternalLinkAlt size={12}/> Abrir despacho
                            </button>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className={"items-center"}>
                    {
                        detail?.inProgress &&
                        <Alert target={"warning"}>
                            Não é possível alterar, pois existe um despacho em andamento.
                        </Alert>
                    }
                    {
                        detail?.assigment &&
                        <Alert target={"warning"}>
                            Não é possível alterar, pois existe uma atribuição em andamento.
                        </Alert>
                    }

                    <Button target={"success"} onClick={openExecution} disabled={disableButtons}>
                        Finalizar agendamento
                    </Button>
                    <Button
                        target={"danger"}
                        onClick={remove}
                        disabled={disableButtons}
                        actionFeedbackType="delete"
                        deferActionFeedback
                        loadingText="Cancelando..."
                    >
                        Cancelar agendamento
                    </Button>
                    <Button onClick={save} loading={isSaving} disabled={disableButtons} actionFeedbackType="save">
                        Salvar
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal open={executionOpen} setOpen={setExecutionOpen} title={"Finalizar agendamento"} className={"w-3xl"} overflow={true}>
                <Modal.Body className={"grid grid-cols-12 gap-4"}>
                    <SelectComponent
                        classNameWrapper={"col-span-7"}
                        value={execution.executionUserId}
                        options={agents ?? []}
                        label={"Realizado por *"}
                        onChange={(value) => setExecution(prev => ({...prev, executionUserId: value?.value ?? ""}))}
                    />

                    <InputDatetime
                        classNameWrapper={"col-span-5"}
                        id={"executionDt"}
                        label={"D/H da execução *"}
                        dateFormat={"LL"}
                        returnDateObj={false}
                        selected={execution.executionDt}
                        onChange={(value) => setExecution(prev => ({...prev, executionDt: value ?? ""}))}
                    />

                    <InputTextarea
                        className={"col-span-12"}
                        textAreaClass={"h-20!"}
                        id={"executionDescription"}
                        label={"Descrição"}
                        value={execution.executionDescription}
                        onChange={({target: {value}}) => setExecution(prev => ({...prev, executionDescription: value}))}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        onClick={confirmExecution}
                        loading={isSaving}
                        actionFeedbackType="save"
                        loadingText="Finalizando..."
                        successText="Finalizado com sucesso"
                    >
                        Finalizar
                    </Button>
                </Modal.Footer>
            </Modal>

            <ModalReverseGeocoding
                open={mapOpen}
                setOpen={setMapOpen}
                address={form}
                setIsLoading={() => {}}
                onConfirm={(value) => updateLatLng(value)}
            />
        </>
    );
};

export default CitizenScheduledVisitEditModal;